import { AccountInfo, AuthenticationResult } from "@azure/msal-browser";
import { IMsalProvider } from "./IMsalProvider";
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig, loginRequest } from "./MsalConfig";
import { appContext } from "../AppContext";
import { SecurityProviderEnum } from "../Providers.LocalStorage/Models";

export class MsalProvider implements IMsalProvider
{
    public msalClient?: PublicClientApplication;

    public async initialise(clientId?: string, tenantDomain?: string, securityProviderType?: SecurityProviderEnum): Promise<void>
    {
        const config = appContext().localStorageProvider.getAadConfig();
        clientId = clientId ?? config.clientId;
        tenantDomain = tenantDomain ?? config.tenantDomain;
        securityProviderType = securityProviderType ?? config.securityProviderType;

        if (!clientId || !tenantDomain)
        {
            this.msalClient = undefined;
            return;
        }

        appContext().localStorageProvider.setAadConfig({ clientId: clientId, tenantDomain: tenantDomain, securityProviderType: securityProviderType });
        msalConfig.auth.clientId = clientId;
        msalConfig.auth.authority = this.getAuthority(tenantDomain, securityProviderType);
        msalConfig.auth.knownAuthorities = (securityProviderType === 'B2C' ? [this.getExternalIdHost(tenantDomain)] : []);
        this.msalClient = new PublicClientApplication(msalConfig);
        await this.msalClient.initialize();
    }

    /**
     * Workforce (AAD) tenants authenticate against login.microsoftonline.com, whereas Entra External ID
     * (B2C) tenants authenticate against their own <tenant>.ciamlogin.com host.
     */
    private getAuthority(tenantDomain: string, securityProviderType?: SecurityProviderEnum): string
    {
        if (securityProviderType === 'B2C')
        {
            return `https://${this.getExternalIdHost(tenantDomain)}/${tenantDomain}/`;
        }
        return `https://login.microsoftonline.com/${tenantDomain}/`;
    }

    private getExternalIdHost(tenantDomain: string): string
    {
        const tenantName = tenantDomain.split('.')[0];
        return `${tenantName}.ciamlogin.com`;
    }

    public login(email: string): Promise<AuthenticationResult>
    {
        if (!this.msalClient)
        {
            throw new Error('MSAL client not initialised.');
        }
        loginRequest.loginHint = email;
        return this.msalClient.loginPopup(loginRequest);
    }

    public getAccount(): (AccountInfo | undefined)
    {
        return this.msalClient?.getAllAccounts()[0];
    }

    public async getToken(): Promise<AuthenticationResult>
    {
        if (!this.msalClient)
        {
            throw new Error('MSAL client not initialised.');
        }
        const account = this.msalClient.getAllAccounts()[0];

        const authResult = await this.msalClient.acquireTokenSilent({
            account: account,
            scopes: [`api://${msalConfig.auth.clientId}/User.Read`],
        });
        return authResult;
    }
}
